import { Injectable } from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {DroppLocationModel, DroppLocationResponse} from '../../models/integrations/dropp/dropp-location.model';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {map} from 'rxjs/operators';
import {DroppZipResponse} from '../../models/integrations/dropp/dropp-zip.model';
import {DroppPriceResponse} from '../../models/integrations/dropp/dropp-price.model';
import {TranslateService} from '@ngx-translate/core';
import {ShippingInfoModel} from '../../platform/payment-process/billing-ov/shipping-info/shipping-info.component';
import {AuthService} from '../auth.service';

@Injectable({
  providedIn: 'root'
})
export class DroppService {

  baseUrl = environment.droppURL;

  /**
   * Cached dropp locations fetched to find the nearest
   * dropp location
   */
  locResponse: DroppLocationResponse;

  /**
   * The home delivery and flytjandi zip codes supported
   * for delivery
   */
  zipCodes: DroppZipResponse;

  /**
   *
   */
  priceResponse: DroppPriceResponse;

  constructor(private http: HttpClient,
              private authSv: AuthService,
              private tSv: TranslateService) { }

  /**
   * Called to initialize the service and fetch the data
   * required
   */
  fetchData() {

    /**
     * We need to fork join as we need to fetch the locations, zip codes
     * and pricing in order to calculate the pricing.
     */
    const fetchDroppLocations = this.fetchDroppLocations();
    const fetchDroppZips = this.fetchDroppZips();
    const fetchDroppPrices = this.fetchDroppPricing();

    forkJoin([
      fetchDroppLocations,
      fetchDroppZips,
      fetchDroppPrices
    ]).subscribe((data) => {

      /**
       * Location response
       */
      this.locResponse = data[0];
      // We need to sort the locations by ascending order to find the approximate locations
      this.locResponse.locations = this.locResponse.locations.sort((loc1, loc2) => loc1.addressObject.zip - loc2.addressObject.zip);

      /**
       * Zip codes
       */
      this.zipCodes = data[1];

      /**
       *
       */
      this.priceResponse = data[2];

      /**
       * Setup location pricing
       */
      this.setupLocationPricing();

    });

  }

  setupLocationPricing() {

    for (const loc of this.locResponse.locations) {

      /**
       * pricetype 1 is capital area so we need pricing for that
       */
      if (loc.pricetype === 1) {

        loc.price = this.priceResponse.droppcapital[0].price;

        /**
         * Pricetype 2 is outside of the capital
         */
      } else if (loc.pricetype === 2) {

        loc.price = this.priceResponse.droppoutside[0].price;

      }

    }

  }

  findShippingOptions(zip: number): DroppLocationModel[] {

    let locations = [];

    if (!this.locResponse || !this.zipCodes) {
      return ;
    }

    /**
     * Check if we can find a zip code which matches exactly to send back
     */
    const zipLocations = this.locResponse.locations.filter((loc) => loc.addressObject.zip === zip);
    locations = locations.concat(zipLocations);

    // Check if supports home delivery
    if (this.zipCodes.supportsHomeDelivery(zip)) {
      // Add home delivery
      // Check if capital to add home delivery during day or afternoon

    } else if (this.zipCodes.supportsFlytjandiDelivery(zip)) {
      // Add samskip / flytjandi delivery

    }

    const closeLocations = this.findDroppLocationsAroundZip(zip, zipLocations).sort((loc1, loc2) => loc1.addressObject.zip - loc2.addressObject.zip);
    locations = locations.concat(closeLocations);

    return locations;

  }

  findDroppLocationsForZip(zip: number): DroppLocationModel[] {

    return this.locResponse.locations.filter((loc) => loc.addressObject.zip === zip);

  }

  /**
   * Finds other dropp locations which have a zip close by. Takes elements above and below.
   * in the zip code range.
   *
   * @param zip The zip code to search by
   * @param excludeLocations The locations already selected which need to be excluded from the list
   * @param numLocations The number of locations to find extra close to the zip code
   */
  findDroppLocationsAroundZip(zip: number, excludeLocations: DroppLocationModel[], numLocations = 3): DroppLocationModel[] {

    const closeLocations = [];

    /**
     * We need to remove the locations which have already been matched to
     * have the same zip code so we do not have duplicates
     */
    const locations = this.locResponse.locations.slice();

    for (const locToRemove of excludeLocations) {

      for (let i = 0; i < locations.length; i++) {

        const loc = locations[i];

        if (loc.id === locToRemove.id) {
          locations.splice(i, 1);
        }
      }

    }

    /**
     * The index where the zip is positioned for the first time above in the list
     */
    let zipIndex = 0;
    for (const loc of locations) {

      if (loc.addressObject.zip > zip) {
        break;
      }
      zipIndex++;

    }

    /**
     * If we happen to select a zip code which is higher than the
     * highest range we need to bring it down in order to be bale to suggest
     * the two highest zip codes
     */
    if (zipIndex === locations.length) {
      zipIndex--;
    }

    /**
     * We go fill the close locations add to the array based on the zip location
     * where we add progressively an item aboe and below except the zip location
     * is at the end where we need custom logic.
     */
    for (let i = 0; i < numLocations && zipIndex < locations.length && zipIndex >= 0; i++) {

      const loc = locations[zipIndex];
      closeLocations.push(loc);
      locations.splice(zipIndex, 1);

      /**
       * If we find the record near or at the end of an array
       * we need to decremenet so we can keep adding to the
       * array. We have a check for the zip index being above
       * 0 so we do not have an infinite loop.
       */
      if (zipIndex > locations.length) {
        zipIndex--;
      } else if (i % 2 === 0) {
        zipIndex--;
      }

    }

    return closeLocations;

  }

  /**
   * Finds home delivery options if they are available for the the given zip
   * code
   *
   * @param zip The zip code to find other methods
   */
  findHomeDeliveryOptions(zip: number): DroppLocationModel[] {

    const deliveries = [];

    const areaCode =  this.zipCodes.findZip(zip);
    if (areaCode) {

      const homeDelivery = new DroppLocationModel();
      homeDelivery.id = '9ec1f30c-2564-4b73-8954-25b7b3186ed3';
      homeDelivery.name = this.tSv.instant('checkout.droppHomeDeliveryItemTitle');
      homeDelivery.address = this.tSv.instant('checkout.droppHomeDeliveryItemSubtitle');
      homeDelivery.dayDelivery = false;

      if (areaCode.capital) {

        homeDelivery.price = this.priceResponse.homecapital[0].price;

        const dayHomeDelivery = new DroppLocationModel();
        dayHomeDelivery.id = '9ec1f30c-2564-4b73-8954-25b7b3186ed3';
        dayHomeDelivery.name = this.tSv.instant('checkout.droppHomeDeliveryDayItemTitle');
        dayHomeDelivery.address = this.tSv.instant('checkout.droppHomeDeliveryDayItemSubtitle');
        dayHomeDelivery.price = this.priceResponse.dayhomecapital[0].price;
        dayHomeDelivery.dayDelivery = true;

        deliveries.push(homeDelivery);
        deliveries.push(dayHomeDelivery);

      } else {

        homeDelivery.price = this.priceResponse.homeoutside[0].price;
        deliveries.push(homeDelivery);

      }

    }

    return deliveries;

  }

  findFlytjandiDeliveryOptions(zip: number): DroppLocationModel {

    if (this.zipCodes.supportsFlytjandiDelivery(zip)) {

      const location = new DroppLocationModel();
      location.id = 'a178c25e-bb35-4420-8792-d5295f0e7fcc';
      location.name = 'Samskip';
      location.address = this.tSv.instant('checkout.droppSamskipItemSubtitle');
      location.price = this.priceResponse.otherlocations[0].price;

      return location;

    }

    return null;

  }

  /**
   * Finds flytjandi / samskip delivery option if it is available
   */

  /**
   * Fetches the locations to deliver to from dropp
   */
  fetchDroppLocations(): Observable<DroppLocationResponse> {

    return this.http.get(`${this.baseUrl}/dropp/api/v1/dropp/locations?store=a6020f49-9702-4522-ae66-b3024d5c8b51`)
      .pipe(map((response) => {

        return DroppLocationResponse.generate(response);

    }));

  }

  /**
   * Fetches the zip codes where home delivery is possible
   * and where dropp is possible
   */
  fetchDroppZips(): Observable<DroppZipResponse> {

    return this.http.get(`${this.baseUrl}/dropp/api/v1/dropp/location/deliveryzips`)
      .pipe(map((response) => {

        return DroppZipResponse.generate(response);

    }));

  }

  /**
   * Fetches the different prices for different methods
   */
  fetchDroppPricing(): Observable<DroppPriceResponse> {

    return this.http.get(`${environment.evoApi}/dropp/prices`).pipe(map((response) => {

      return DroppPriceResponse.generate(response['data']);

    }));

  }

  /**
   *
   */
  createDroppOrder(hostedPageId: string, bookName: string, shipping: ShippingInfoModel): Observable<true> {

    console.log('shipping location day delivery: ' + JSON.stringify(shipping.location.dayDelivery, null, 4));
    const body = {
      locationId: shipping.location.id,
      dayDelivery: shipping.location.dayDelivery,
      hostedPageId,
      productName: bookName,
      address: shipping.address,
      zip: shipping.zip,
      city: shipping.city,
      phone: shipping.phone
    };

    return this.authSv.post(`/dropp/orders`, body).pipe(map((response) => {
      return response['data'];
    }));

  }
}
